/* External */
import {
  useState, memo, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import Icon from '@dtvgo/icon';

/* Utils */
import FocusableInput from '../../../../components/FocusableInput';
import FocusableButtonProfile from '../../../../components/FocusableButtonProfile';
import { useOnBackPress } from '../../../../utils/keyDownEvents';
import { Routes, getLink } from '../../../../utils/router';
import { TYPE_ACTIONS_PROFILE } from '../../../../utils/constants';
import { useCountry } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../utils/googleTags';

/* Storage */
import DefaultKeyboard from '../../../../components/DefaultKeyboard';

/* Style */
import './CreateProfileName.scss';
import { formatName } from '../../../../utils/profiles';
import { saveProfileName } from '../../../../storage/profiles/actions';

function CreateProfileName({ realFocusKey, setFocus }) {
  const [name, setName] = useState('');
  const history = useHistory();
  const params = useParams();
  const innerRef = useRef();
  const location = useLocation();
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const [profiles] = useState(userProfiles);
  const selectedProfile = profiles?.find((profile) => profile?.id === params.id);
  const createProfileFocusKey = `${realFocusKey}-first-focus-create-profile`;
  const newProfileName = `Perfil ${(profiles?.length || 0) + 1}`;
  const [showKeyboard, setShowKeyboard] = useState(false);
  const indexColor = location.state?.indexColor;
  const typeCreateName = location.state?.typeCreateName;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const [warn, setWarn] = useState(false);
  const maxLenght = 16;
  const dispatch = useDispatch();

  const profileCommonTagEvent = useCallback(({ subCategory, eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: subCategory || GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE_NAME,
        eventName,
      });
    }
  }, [commonTaggingEvent]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      if (typeCreateName === TYPE_ACTIONS_PROFILE.EDIT) {
        profileCommonTagEvent({
          eventName: GTM_EVENT_NAMES.PROFILES_CREATE_NAME.PAGE_VIEW_EDIT,
          subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE_NAME,
        });
      } else if (typeCreateName === TYPE_ACTIONS_PROFILE.ONBOARDING) {
        profileCommonTagEvent({
          eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.PAGE_VIEW_CREATE_NAME,
          subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_ONBOARDING,
        });
      } else {
        profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      }
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, country, typeCreateName]);

  useEffect(() => {
    if (params.id) {
      setName(selectedProfile.name);
    } else {
      setName(newProfileName);
    }
  }, [newProfileName, params.id, selectedProfile?.name]);

  useEffect(() => {
    if (name.length === 0) {
      setWarn(true);
    } else {
      setWarn(false);
    }

    innerRef.current.value = name;
  }, [name]);

  useEffect(() => {
    setFocus(createProfileFocusKey);
  }, [createProfileFocusKey, setFocus]);

  const onInput = useCallback((text) => {
    const profileName = text.target.value;
    setName(formatName(profileName));
  }, []);

  const validationButton = useCallback(() => (
    !name
    || name.replace(/\s/g, '').length === 0
  ), [name]);

  const handleClick = useCallback(({ id }) => {
    if (validationButton() === false && name) {
      dispatch(saveProfileName(name));
      if (params.id) {
        history.push(getLink(Routes.Private.Profiles.Edit, { id }), { indexColor, name });
      } else {
        profileCommonTagEvent({
          subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE_NAME,
          eventName: GTM_EVENT_NAMES.PROFILES_CREATE_NAME.CONTINUE_PROFILE,
        });

        history.push(getLink(Routes.Private.Profiles.Create.Resume, { name }), { indexColor });
      }
    }
  }, [history, validationButton, name, dispatch, indexColor, params, profileCommonTagEvent]);

  const focusInput = useCallback(() => {
    if (document.activeElement === innerRef.current) {
      innerRef.current.blur();
      setShowKeyboard(false);
    } else {
      setShowKeyboard(true);
      setFocus('KeyboardFocusable');
    }
  }, [setFocus]);

  function inputValue() {
    if (name) {
      return name;
    }
    return params.id ? selectedProfile.name : newProfileName;
  }

  const closeKeyboard = useCallback(() => {
    innerRef.current.blur();
    setShowKeyboard(false);
    setFocus(createProfileFocusKey);
  }, [createProfileFocusKey, setFocus]);

  const handleBackPress = useCallback(() => {
    localStorage.removeItem('username');
    if (typeCreateName === TYPE_ACTIONS_PROFILE.EDIT) {
      profileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_EDIT.BACK_PROFILES,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_EDIT,
      });
    } else {
      profileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_CREATE.BACK_PROFILES,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
      });
    }
    if (showKeyboard === true) {
      innerRef.current.blur();
      setShowKeyboard(false);
      setFocus(createProfileFocusKey);
    } else {
      history.push(getLink(Routes.Private.Profiles));
    }
  }, [
    createProfileFocusKey,
    history,
    setFocus,
    showKeyboard,
    profileCommonTagEvent,
    typeCreateName,
  ]);

  useOnBackPress({ listener: handleBackPress });

  return (
    <div className="dtv-html5-c-create-profile">
      <div className="dtv-html5-c-create-profile--div">
        <h1>
          {profiles?.length > 0 ? (<Icon className="dtv-html5-c-create-profile__icon-back-logo" name="arrow-back" />) : ''}
          {selectedProfile?.id ? <FormattedMessage id="profile.change" /> : <FormattedMessage id="profile.create" />}
        </h1>
        <form className="dtv-html5-c-create-profile--div-form">
          <FocusableInput
            id="namePerfil"
            type="text"
            name="namePerfil"
            required
            labelContent={<FormattedMessage id="profile.call.name" />}
            createUser
            isInputActive
            onInput={onInput}
            inputValue={() => { inputValue(); }}
            onEnterPress={focusInput}
            innerRef={innerRef}
            focusKey={createProfileFocusKey}
            disabled
            emptyInputText={<FormattedMessage id="profiles.userProfile.editProfile.emptyInputText" />}
            warn={warn}
            warnMsg={<FormattedMessage id="profiles.userProfile.editProfile.emptyInputText" />}
          />
          {profiles?.length === 0 ? (
            <p>
              <FormattedMessage id="profile.main" />
            </p>
          ) : ''}
          {showKeyboard ? (
            <DefaultKeyboard
              className={showKeyboard ? 'show' : 'hide'}
              confirm={closeKeyboard}
              deleteCharacter={() => setName((prevName) => prevName.slice(0, -1))}
              addSpace={() => { setName((prevName) => `${prevName} `); }}
              addCharacter={(e) => {
                setName((prevName) => {
                  const newName = prevName + e;
                  return newName.length <= maxLenght ? newName : prevName;
                });
              }}
              blockNavigationOut
              focusKey="KeyboardFocusable"
            />
          ) : ''}
          <div className="dtv-html5-c-create-profile--div-buttonAlignment">
            <FocusableButtonProfile
              id={params.id}
              className={validationButton() ? 'dtv-btn-tv dtv-html5-c-create-profile__button' : 'dtv-btn-blue dtv-html5-c-create-profile__button'}
              secondGray
              bordered
              disabled={validationButton()}
              onEnterPress={handleClick}
            >
              <FormattedMessage id="common.continue" />
            </FocusableButtonProfile>
          </div>
        </form>
      </div>
    </div>
  );
}

CreateProfileName.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(CreateProfileName)));
